import React from "react";

/**
 * ErrorBoundary — catches uncaught render/lifecycle errors in its subtree
 * so one throwing component degrades to a fallback instead of white-screening
 * the whole SPA (the classic "Application error: a client-side exception has
 * occurred" failure mode).
 *
 * Props:
 *   - fallback: ReactNode | (error) => ReactNode. If omitted, a minimal
 *     branded "something went wrong" panel is shown.
 *   - onError: optional (error, info) => void hook.
 *   - silent: if true, render nothing on error (use for decorative subtrees
 *     where a fallback panel would be worse than just hiding the feature).
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Never swallow silently — the whole point is that the error is SEEN.
    // Log to console, and to PostHog if it's available, so a degraded render
    // still surfaces in analytics rather than vanishing.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] caught a render error:", error, info?.componentStack);
    try {
      window.posthog?.capture("client_error_boundary", {
        message: String(error?.message || error),
        component_stack: (info?.componentStack || "").slice(0, 500),
      });
    } catch {}
    this.props.onError?.(error, info);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    if (this.props.silent) return null;
    if (this.props.fallback !== undefined) {
      return typeof this.props.fallback === "function"
        ? this.props.fallback(this.state.error)
        : this.props.fallback;
    }

    // Default branded fallback — keeps the user oriented and offers a way out
    // instead of a blank page.
    return (
      <div
        role="alert"
        style={{
          minHeight: "60vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "16px",
          padding: "48px 24px",
          textAlign: "center",
          fontFamily: "Georgia, serif",
          color: "#1A1A1A",
        }}
      >
        <p
          style={{
            fontFamily: '"JetBrains Mono", ui-monospace, monospace',
            fontSize: "11px",
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            color: "#8a8a8a",
            margin: 0,
          }}
        >
          Something glitched
        </p>
        <h1 style={{ fontSize: "clamp(28px, 5vw, 44px)", lineHeight: 1.1, margin: 0, fontStyle: "italic" }}>
          This bit didn't load.
        </h1>
        <p style={{ color: "#5a5a5a", maxWidth: "44ch", margin: 0, fontFamily: "system-ui, sans-serif", fontSize: "15px" }}>
          The rest of the site is fine. Reload to try again, or head back home.
        </p>
        <div style={{ display: "flex", gap: "12px", marginTop: "8px", flexWrap: "wrap", justifyContent: "center" }}>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={btn(true)}
          >
            Reload
          </button>
          <a href="/" style={btn(false)}>Go home</a>
        </div>
      </div>
    );
  }
}

function btn(primary) {
  return {
    display: "inline-flex",
    alignItems: "center",
    padding: "12px 20px",
    borderRadius: "999px",
    fontFamily: '"JetBrains Mono", ui-monospace, monospace',
    fontSize: "11px",
    letterSpacing: "0.22em",
    textTransform: "uppercase",
    textDecoration: "none",
    cursor: "pointer",
    border: "2px solid #1A1A1A",
    background: primary ? "#1A1A1A" : "transparent",
    color: primary ? "#FAFAFA" : "#1A1A1A",
  };
}
